import { Product, ProductStoreRequire } from "@/types/product";
import { App, Button, Flex, notification, TableColumnsType } from "antd";
import { FC, useEffect, useRef, useState } from "react";
import { productReqDel, productReqList } from "@/apis/product";
import { useTranslation } from "react-i18next";
import useDataList from "@/hooks/useDataList";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import { AnyObject } from "antd/lib/_util/type";
import SText from "@/styles/SText";
import _ from "lodash";
import { CommonListParams } from "@/types/common";
import useSourceType from "@/hooks/useSourceType";
import { numFormmater } from "@/pages/Requirement/util";
import ProductStoreReqModal from "./ProductStoreReqModal";
import { SConModal } from "@/styles/SConModal";

interface ProductStoreReqListProps {
  product: Product;
}

const ProductStoreReqList: FC<ProductStoreReqListProps> = ({ product }) => {
  const { t } = useTranslation();
  const { getProductStore } = useSourceType();

  const tableRef = useRef<TableLayoutRef | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [detail, setDetail] = useState<ProductStoreRequire | null>(null);
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [delLoading, setDelLoading] = useState<string>('');

  const [list, total, loading, { fetchList, addItem, updateItem, rmItem }] = useDataList<ProductStoreRequire, [string, CommonListParams?]>({ fetchApi: productReqList });

  // ✅ 组件卸载时取消请求
  useEffect(() => {
    !!product && fetch(0);
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handelDel = async (id: string) => {
    try {
      setDelLoading(id);
      await productReqDel(product.id, id);
      rmItem(id);
      notification.success({ message: t('common.rmSuccess') })
    } catch (error) {
      notification.error({ message: t('common.rmError') });
    }
    setDelLoading('');
  };

  const columns: TableColumnsType<ProductStoreRequire> = [
    {
      width: 100,
      title: t("product.code"),
      dataIndex: "productCode",
      render: () => <SText style={{ width: '100%' }} text={product?.code} />
    },
    {
      width: 120,
      title: t("product.name"),
      dataIndex: "productName",
      render: () => <SText style={{ width: '100%' }} text={product?.name} />
    },
    {
      width: 140,
      title: t("product.brand1"),
      dataIndex: "productModel1",
      render: () => <SText style={{ width: '100%' }} text={product?.model} />
    },
    {
      width: 140,
      title: t("product.brand2"),
      dataIndex: "productModel2",
      render: () => <SText style={{ width: '100%' }} text={product?.t1 ?? ''} />
    },
    {
      width: 100,
      title: t("product.uom"),
      dataIndex: "productUom",
      render: () => <SText text={product?.uom} />,
    },
    {
      width: 100,
      title: t("product.storageCode"),
      dataIndex: "storageCode",
      render: (_, record) => {
        let info = getProductStore(record.storageCode);
        return info && <SText tagMode text={info.label} color={info.color} />
      },
    },
    {
      width: 100,
      title: t("product.minValue"),
      dataIndex: "minValue",
      render: (_, record) => record.minValue && <SText text={numFormmater(record.minValue)} />,
    },
    {
      width: 100,
      title: t("product.maxValue"),
      dataIndex: "maxValue",
      render: (_, record) => record.maxValue && <SText text={numFormmater(record.maxValue)} />,
    },
    {
      width: 80,
      title: t("product.unit"),
      dataIndex: "unit",
      render: (_, record) => <SText text={record.unit} />,
    },
    {
      width: 140,
      title: t("product.inspectionCycleDays"),
      dataIndex: "inspectionCycleDays",
      render: (_, record) => record.inspectionCycleDays && <SText text={numFormmater(record.inspectionCycleDays)} />,
    },
    {
      width: 120,
      title: t("order.remark"),
      dataIndex: "unit",
      render: (_, record) => <SText text={record.remark} />,
    },
    {
      width: 160,
      title: t("common.opt"),
      fixed: "right",
      render: (_, item) => (
        <Flex gap={6}>
          <Button onClick={() => { setDetail(item) }} type='link'
          >{t("product.modify")}</Button>
          <SConModal onOk={() => handelDel(item.id)} title={t('common.tip')} content={t('common.rmContent')} >
            <Button danger type="link" loading={delLoading === item.id}>{t('common.del')}</Button>
          </SConModal>
        </Flex>
      )
    },
  ];

  const fetch = (offset: number) => {
    // ✅ 取消上一次请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // ✅ 创建新的 AbortController
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;

    fetchList(product?.id ?? '', {
      pagination: {
        take,
        offset
      }
    }).catch((err) => {
      // ✅ 忽略取消的请求
      if (err?.name === 'AbortError') {
        return;
      }
    });
  };

  const handleClose = () => {
    setAddOpen(false);
    setDetail(null);
  };

  const handleSucc = (data: ProductStoreRequire) => {
    if (!!detail) {
      updateItem(data);
      setDetail(null);
    } else if (addOpen) {
      addItem(data);
      setAddOpen(false);
    }
  };

  const headerChildren = (
    <Flex>
      <Button type="primary" onClick={() => setAddOpen(true)} >{t('common.add')}</Button>
    </Flex>
  );

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <STableLayout
        total={total}
        loading={loading}
        dataSource={list}
        columns={columns as Array<AnyObject>}
        tableLayoutRef={tableRef}
        handleChange={fetch}
        headerChildren={headerChildren}
        extHeight={40}
        scrollX={1000}
      />
      {
        (!!detail || addOpen) && <ProductStoreReqModal product={product}
          detail={detail}
          open={!!detail || addOpen}
          onClose={handleClose}
          onSuccess={handleSucc} />
      }
    </Flex>
  );
};

export default ProductStoreReqList;
